/**
 * usePhotoComments Hook
 * 
 * Manages comments for a photo.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { PhotoComment } from "../types";

interface UsePhotoCommentsReturn {
  comments: PhotoComment[];
  commentsCount: number;
  isLoading: boolean;
  isSending: boolean;
  addComment: (content: string) => Promise<boolean>;
  editComment: (commentId: string, content: string) => Promise<boolean>;
  deleteComment: (commentId: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage photo comments
 */
export const usePhotoComments = (photoId: string): UsePhotoCommentsReturn => {
  const { user, activeProfile, roles } = useAuth();
  const userId = activeProfile?.id || user?.id;
  
  const [comments, setComments] = useState<PhotoComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  /**
   * Fetch comments for the photo
   */
  const fetchComments = useCallback(async () => {
    if (!photoId) return;

    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("photo_comments")
        .select(`
          *,
          user:profiles(id, full_name, nickname, avatar_url)
        `)
        .eq("photo_id", photoId)
        .eq("is_deleted", false)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setComments(data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
      setIsLoading(false);
    }
  }, [photoId]);

  /**
   * Add a new comment
   */
  const addComment = useCallback(async (content: string): Promise<boolean> => {
    if (!userId || !photoId || !content.trim()) return false;

    try {
      setIsSending(true);

      const { data, error } = await supabase
        .from("photo_comments")
        .insert({
          photo_id: photoId,
          user_id: userId,
          content: content.trim(),
        })
        .select(`
          *,
          user:profiles(id, full_name, nickname, avatar_url)
        `)
        .single();

      if (error) throw error;

      setComments(prev => [...prev, data]);
      return true;
    } catch (err) {
      console.error("Error adding comment:", err);
      return false;
    } finally {
      setIsSending(false);
    }
  }, [photoId, userId]);

  /**
   * Edit a comment
   */
  const editComment = useCallback(async (commentId: string, content: string): Promise<boolean> => {
    if (!userId || !content.trim()) return false;

    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment || comment.user_id !== userId) return false;

      const { error } = await supabase
        .from("photo_comments")
        .update({
          content: content.trim(),
          is_edited: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", commentId);

      if (error) throw error;

      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, content: content.trim(), is_edited: true }
          : c
      ));

      return true;
    } catch (err) {
      console.error("Error editing comment:", err);
      return false;
    }
  }, [comments, userId]);

  /**
   * Delete a comment (soft delete)
   */
  const deleteComment = useCallback(async (commentId: string): Promise<boolean> => {
    try {
      const comment = comments.find(c => c.id === commentId);
      if (!comment) return false;

      // Check permission
      if (comment.user_id !== userId && !roles.isSuperAdmin) {
        return false;
      }

      const { error } = await supabase
        .from("photo_comments")
        .update({ is_deleted: true, updated_at: new Date().toISOString() })
        .eq("id", commentId);

      if (error) throw error;

      setComments(prev => prev.filter(c => c.id !== commentId));
      return true;
    } catch (err) {
      console.error("Error deleting comment:", err);
      return false;
    }
  }, [comments, userId, roles.isSuperAdmin]);

  /**
   * Refresh comments
   */
  const refresh = useCallback(async () => {
    await fetchComments();
  }, [fetchComments]);

  // Fetch on mount
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Realtime subscription for new comments
  useEffect(() => {
    if (!photoId) return;

    const channel = supabase
      .channel(`photo-comments-${photoId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "photo_comments",
          filter: `photo_id=eq.${photoId}`,
        },
        async (payload) => {
          // Fetch the new comment with user data
          const { data } = await supabase
            .from("photo_comments")
            .select(`*, user:profiles(id, full_name, nickname, avatar_url)`)
            .eq("id", payload.new.id)
            .single();

          if (data && !comments.some(c => c.id === data.id)) {
            setComments(prev => [...prev, data]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [photoId, comments]);

  return {
    comments,
    commentsCount: comments.length,
    isLoading,
    isSending,
    addComment,
    editComment,
    deleteComment,
    refresh,
  };
};

